<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 align-items-end d-flex pb-2 justify-content-end"
    />
    <filter-manager
      v-model="filter.model"
      :filters="filtersHeader"
      class="filter-manager_extended"
      search-container-class="search-btn col-lg-2-custom"
      @center="val => getMunicipalities(val?.id)"
      @municipality="val => getSchools(val?.id)"
      @search="() => onSearchHandler()"
    />

    <custom-separator
      text="Resultado de las Solicitudes de Transferencia de Libros"
      class="my-4 custom-separator-line"
    />

    <div v-if="hasResults" class="search-result">
      <tranfer-result-row
        v-for="(item, index) in results"
        :key="index"
        :item="item"
        :actions-to-show="[]"
        @open="handlerOpen"
        @close="handlerClose"
        @refresh="() => onSearchHandler(pagination.currentPage)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onSearchHandler"
      />
    </div>

    <custom-result-not-found
      v-else
      class="mt-4 row pt-3"
      text="No se ha encontrado resultado de las solicitudes de transferencia."
    />
  </div>
</template>

<script>
import PageHeader from "PageHeader";
import FilterManager from "FilterManager";
import Pagination from "pagination";
import CustomResultNotFound from "custom-result-not-found";
import CustomSeparator from "custom-separator";

import storeMixin from "@/mixin/storeGenerator";

import * as bookService from "@/modules/books-requests/api";
import locationService from "@/services/api/location.api";
import inventoryCatalogService from "@/services/api/inventorycatalog.api";
import inventoryTransferService from "@/services/api/inventoryTransfer.api";
import academicService from "@/services/api/academicUtils.api.js";
import EduEspCode from "@/utils/constants/eduEspCode";
import EduEspTextCodes from "@/utils/constants/EduEspTextCodes";
import TranferResultRow from "./components/TransferResultRow";

const DEFAULT_PAGE_SIZE = 10;

export default {
  name: "InventoryTranferBooks",
  components: {
    PageHeader,
    FilterManager,
    Pagination,
    TranferResultRow,
    CustomResultNotFound,
    CustomSeparator
  },
  mixins: [storeMixin],
  data() {
    return {
      results: [],
      filter: {
        model: {
          catalog: null,
          subject: null,
          grade: null,
          status: null,
          publisher: null,
          author: null,
          readingMedium: null,
          center: null,
          municipality: null,
          school: null,
          requestDate: null,
          requestNumberOrCode: null
        },
        options: {
          catalogs: [],
          subjects: [],
          grades: [],
          statuses: [],
          publishers: [],
          authors: [],
          readingMediums: [],
          centers: [],
          municipalities: [],
          schools: []
        }
      },
      pagination: {
        count: 0,
        currentPage: 1,
        pageCount: 1,
        totalItemCount: 0,
        pageSize: DEFAULT_PAGE_SIZE
      }
    };
  },
  async mounted() {
    await Promise.all([
      this.getCatalogs(),
      this.getSubjects(),
      this.getGrades(),
      this.getStatuses(),
      this.getPublishers(),
      this.getAuthors(),
      this.getReadingMediums(),
      this.getCenters()
    ]);
  },
  computed: {
    page() {
      return {
        icon: "fal fa-exchange",
        title: "Transferencia de Libros",
        routes: ["Inicio", "Libros", "Transferencia de Libros"]
      };
    },
    headerActionConfig() {
      return [
        {
          text: "Regresar",
          icon: "fas fa-arrow-circle-left",
          variant: "outline-success",
          click: () => this.$router.back()
        }
      ];
    },
    academicYearId() {
      return this.currentAcademicYear().id;
    },
    filtersHeader() {
      return [
        {
          name: "catalog",
          component: "CustomDropDown",
          options: this.filter.options.catalogs,
          value: this.filter.model.catalog,
          placeholder: "Título del Libro",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-sm-6 col-md mx-0",
          clearable: true
        },

        {
          name: "grade",
          component: "CustomDropDown",
          options: this.filter.options.grades,
          value: this.filter.model.grade,
          placeholder: "Grado",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-sm-6 col-md mx-0",
          clearable: true
        },
        {
          name: "status",
          component: "CustomDropDown",
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: "Estatus",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-sm-6 col-md mx-0",
          clearable: true
        },
        {
          name: "requestNumberOrCode",
          component: "CustomInput",
          placeholder: "Escriba Número de Solicitud o Código de Libro",
          value: this.filter.model.requestNumberOrCode,
          icon: "fa fa-search orange-icon",
          leftIcon: true,
          class: "col-12 col-sm-6 col-md-5 mx-0",
          type: "text"
        },
        {
          name: "publisher",
          component: "CustomDropDown",
          value: this.filter.model.publisher,
          options: this.filter.options.publishers,
          placeholder: "Casa Editorial",
          class: "col-12 col-sm-6 col-md-4 mb-3",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          isAdvanced: true
        },
        {
          name: "author",
          component: "CustomDropDown",
          value: this.filter.model.author,
          options: this.filter.options.authors,
          placeholder: "Autor",
          class: "col-12 col-sm-6 col-md-4",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          isAdvanced: true
        },
        {
          name: "readingMedium",
          component: "CustomDropDown",
          value: this.filter.model.readingMedium,
          options: this.filter.options.readingMediums,
          placeholder: "Medio de Lectura",
          class: "col-12 col-sm-6 col-md-4",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          isAdvanced: true
        },
        {
          name: "center",
          component: "CustomDropDown",
          value: this.filter.model.center,
          options: this.filter.options.centers,
          placeholder: "Centro",
          class: "col-12 col-sm-6 col-md-4 mb-3",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          isAdvanced: true
        },
        {
          name: "municipality",
          component: "CustomDropDown",
          value: this.filter.model.municipality,
          options: this.filter.options.municipalities,
          placeholder: "Municipio",
          class: "col-12 col-sm-6 col-md-4",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          isAdvanced: true
        },
        {
          name: "school",
          component: "CustomDropDown",
          value: this.filter.model.school,
          options: this.filter.options.schools,
          placeholder: "Escuela",
          class: "col-12 col-sm-6 col-md-4",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          isAdvanced: true
        },
        {
          name: "requestDate",
          component: "CustomDatePicker",
          placeholder: "Fecha de Solicitud",
          class: "col-12 col-sm-6 col-md-4 w-100",
          type: "date",
          isAdvanced: true
        },
        {
          name: "subject",
          component: "CustomDropDown",
          options: this.filter.options.subjects,
          value: this.filter.model.subject,
          placeholder: "Materia",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-sm-6 col-md-4 mx-0",
          clearable: true,
          isAdvanced: true
        }
      ];
    },
    hasResults() {
      return Boolean(this.results.length);
    }
  },
  methods: {
    async onSearchHandler(currentPage = 1) {
      const {
        catalog,
        subject,
        grade,
        status,
        publisher,
        author,
        readingMedium,
        center,
        municipality,
        school,
        requestDate,
        requestNumberOrCode
      } = this.filter.model;

      const { pageSize } = this.pagination;

      const { data } = await inventoryTransferService.search({
        catalogId: catalog?.id,
        subjectId: subject?.id,
        gradeId: grade?.id,
        statusId: status?.id,
        publisherId: publisher?.id,
        authorId: author?.id,
        readingMediumId: readingMedium?.id,
        centerId: center?.id,
        municipalityId: municipality?.id,
        schoolId: school?.id,
        requestNumberOrCode,
        requestDate,
        currentPage,
        pageSize
      });

      this.results = data.data.map(x => ({
        ...x,
        isOpen: false
      }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count
      };
    },
    async getCatalogs() {
      const { data } = await inventoryCatalogService.getKeyValue();
      this.filter.options.catalogs = data;
    },
    async getSubjects() {
      const { data } = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibSubject
      });
      this.filter.options.subjects = data;
    },
    async getGrades() {
      const { data } = await academicService.getGrades();
      this.filter.options.grades = data;
    },
    async getStatuses() {
      const { data } = await inventoryTransferService.getStatuses();
      this.filter.options.statuses = data;
    },
    async getCenters() {
      const { data } = await locationService.getCenters({
        academicYearId: this.academicYearId
      });
      this.filter.options.centers = data;

      if (data.length === 1) {
        this.filter.model.center = data[0];
        this.getMunicipalities(data[0].id);
      }
    },
    async getMunicipalities(centerId) {
      this.filter.model.municipality = null;
      this.filter.model.school = null;
      this.filter.options.municipalities = [];
      this.filter.options.schools = [];

      if (!centerId) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId
      });
      this.filter.options.municipalities = data;

      if (data.length === 1) {
        this.filter.model.municipality = data[0];
        this.getSchools(data[0].id);
      }
    },
    async getSchools(municipalityId) {
      this.filter.model.school = null;
      this.filter.options.schools = [];

      if (!municipalityId) return;

      const academicYearId = this.currentAcademicYear().id;
      const { data } = await locationService.getSchools({
        academicYearId,
        centerId: this.filter.model.center.id,
        municipalityId
      });
      this.filter.options.schools = data;
      if (data.length === 1) this.filter.model.school = data[0];
    },
    async getPublishers() {
      const { data } = await bookService.getBooksPublishers();
      this.filter.options.publishers = data.filter(
        e => e.name.toUpperCase() !== EduEspTextCodes.NotAvailable
      );
    },
    async getAuthors() {
      const { data } = await bookService.getBooksAuthors();
      this.filter.options.authors = data.filter(
        e => e.name.toUpperCase() !== EduEspTextCodes.NotAvailable
      );
    },
    async getReadingMediums() {
      const { data } = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibReadingmedium
      });
      this.filter.options.readingMediums = data;
    },
    async handlerOpen(item) {
      this.results
        .filter(x => x.id !== item.id && x.isOpen)
        .forEach(x => (x.isOpen = false));

      item.isOpen = true;
    },
    handlerClose(item) {
      item.isOpen = false;
    }
  }
};
</script>
<style scoped>
::v-deep .custom-datetime-picker-container.width-container {
  width: 100% !important;
}
@media (min-width: 992px) {
  ::v-deep .col-lg-2-custom {
    -webkit-box-flex: 0 !important;
    -ms-flex: 0 0 16.66666667% !important;
    flex: 0 0 16.66666667% !important;
    max-width: 16.66666667% !important;
  }
}
</style>
